import { formatMontant } from '../../utils/montants';
import { coutMensuelLigne, afficheQuantite } from '../../utils/coutsRecurrents';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Collapse,
  Divider,
  Breadcrumbs,
  Link,
  InputAdornment,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Edit3,
  Save,
  ChevronDown,
  ChevronRight,
  GripVertical,
  DollarSign,
  Calendar,
  FolderKanban,
  Clock,
  Tag
} from 'lucide-react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';

const complexityOptions = [
  { value: '', label: 'Non définie' },
  { value: 'LOW', label: 'Simple' },
  { value: 'MEDIUM', label: 'Moyen' },
  { value: 'HIGH', label: 'Complexe' },
  { value: 'COMPLEX', label: 'Très complexe' }
];

function TemplateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState('');
  
  // Project type configurations (loaded from template.projectType)
  const [categories, setCategories] = useState([]);
  const [hourTypes, setHourTypes] = useState([]);
  const [costTypes, setCostTypes] = useState([]);
  
  // Dialogs
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [newSection, setNewSection] = useState({ categoryId: '', customCategory: '', name: '' });
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    complexity: '',
    hours: [] // Array of { hourTypeId, defaultHours }
  });
  const [editingItemId, setEditingItemId] = useState(null);

  // Recurring cost dialog
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [editingCostId, setEditingCostId] = useState(null);
  const [newCost, setNewCost] = useState({
    costTypeId: '',
    customType: '',
    name: '',
    description: '',
    monthlyCost: 0,
    quantity: 1,
    showQuantity: true,
    isOptional: false,
    isIncluded: false
  });

  const fetchTemplate = useCallback(async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/templates/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplate(data);
        setTempName(data.name);
        
        // Extract configurations from projectType
        if (data.projectType) {
          setCategories(data.projectType.categories || []);
          setHourTypes(data.projectType.hourTypes || []);
          setCostTypes(data.projectType.recurringCostTypes || []);
          
          // Set default for new section
          if (data.projectType.categories?.length > 0) {
            setNewSection(prev => ({ ...prev, categoryId: data.projectType.categories[0].id }));
          }
          if (data.projectType.recurringCostTypes?.length > 0) {
            setNewCost(prev => ({ ...prev, costTypeId: data.projectType.recurringCostTypes[0].id }));
          }
        }
        
        // Expand all sections by default
        const expanded = {};
        data.sections?.forEach(s => { expanded[s.id] = true; });
        setExpandedSections(expanded);
      } else {
        navigate('/admin/templates');
      }
    } catch (err) {
      console.error('Fetch template error:', err);
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  const handleUpdateTemplate = async (updates) => {
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/templates/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      fetchTemplate();
    } catch (err) {
      console.error('Update template error:', err);
    }
  };

  const handleAddSection = async () => {
    if (!newSection.name.trim()) return;

    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/templates/${id}/sections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          categoryId: newSection.categoryId === 'other' ? null : newSection.categoryId,
          customCategory: newSection.categoryId === 'other' ? newSection.customCategory : null,
          name: newSection.name
        })
      });
      setSectionDialogOpen(false);
      setNewSection({ categoryId: categories[0]?.id || '', customCategory: '', name: '' });
      fetchTemplate();
    } catch (err) {
      console.error('Add section error:', err);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Supprimer cette section et tous ses éléments ?')) return;

    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/templates/sections/${sectionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTemplate();
    } catch (err) {
      console.error('Delete section error:', err);
    }
  };

  const initializeItemHours = () => {
    return hourTypes.map(ht => ({
      hourTypeId: ht.id,
      hourTypeName: ht.name,
      defaultHours: 0
    }));
  };

  const openAddItemDialog = (sectionId) => {
    setEditingSectionId(sectionId);
    setEditingItemId(null);
    setNewItem({
      name: '',
      description: '',
      complexity: '',
      hours: initializeItemHours()
    });
    setItemDialogOpen(true);
  };

  const openEditItemDialog = (sectionId, item) => {
    setEditingSectionId(sectionId);
    setEditingItemId(item.id);
    
    // Map existing hours to our format
    const hoursMap = {};
    (item.hours || []).forEach(h => {
      hoursMap[h.hourTypeId] = h.defaultHours || 0;
    });
    
    const hours = hourTypes.map(ht => ({
      hourTypeId: ht.id,
      hourTypeName: ht.name,
      defaultHours: hoursMap[ht.id] || 0
    }));
    
    setNewItem({
      name: item.name,
      description: item.description || '',
      complexity: item.complexity || '',
      hours
    });
    setItemDialogOpen(true);
  };

  const handleSaveItem = async () => {
    if (!newItem.name.trim()) return;

    try {
      const token = localStorage.getItem('portalToken');
      
      // Filter hours with non-zero values
      const hoursToSave = newItem.hours
        .filter(h => h.defaultHours > 0)
        .map(h => ({ hourTypeId: h.hourTypeId, defaultHours: h.defaultHours }));
      
      const payload = {
        name: newItem.name,
        description: newItem.description,
        complexity: newItem.complexity || null,
        hours: hoursToSave
      };
      
      if (editingItemId) {
        await fetch(`/api/templates/items/${editingItemId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      } else {
        await fetch(`/api/templates/sections/${editingSectionId}/items`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      }
      
      setItemDialogOpen(false);
      fetchTemplate();
    } catch (err) {
      console.error('Save item error:', err);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Supprimer cet élément ?')) return;

    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/templates/items/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTemplate();
    } catch (err) {
      console.error('Delete item error:', err);
    }
  };

  // === RECURRING COSTS HANDLERS ===

  const openAddCostDialog = () => {
    setEditingCostId(null);
    const defaultCostType = costTypes[0];
    setNewCost({
      costTypeId: defaultCostType?.id || '',
      customType: '',
      name: '',
      description: '',
      monthlyCost: defaultCostType?.defaultCost || 0,
      quantity: 1,
      showQuantity: true,
      isOptional: false,
      isIncluded: false
    });
    setCostDialogOpen(true);
  };

  const openEditCostDialog = (cost) => {
    setEditingCostId(cost.id);
    setNewCost({
      costTypeId: cost.costTypeId || '',
      customType: cost.customType || '',
      name: cost.name,
      description: cost.description || '',
      monthlyCost: cost.monthlyCost || 0,
      quantity: cost.quantity || 1,
      showQuantity: cost.showQuantity !== false,
      isOptional: cost.isOptional || false,
      isIncluded: cost.isIncluded || false
    });
    setCostDialogOpen(true);
  };

  const handleCostTypeChange = (costTypeId) => {
    const selectedType = costTypes.find(ct => ct.id === costTypeId);
    setNewCost(prev => ({
      ...prev,
      costTypeId,
      monthlyCost: selectedType?.defaultCost || prev.monthlyCost
    }));
  };

  const handleSaveCost = async () => {
    if (!newCost.name.trim()) return;

    try {
      const token = localStorage.getItem('portalToken');
      
      const payload = {
        costTypeId: newCost.costTypeId === 'other' ? null : newCost.costTypeId,
        customType: newCost.costTypeId === 'other' ? newCost.customType : null,
        name: newCost.name,
        description: newCost.description,
        monthlyCost: newCost.monthlyCost,
        quantity: newCost.quantity,
        showQuantity: newCost.showQuantity,
        isOptional: newCost.isOptional,
        isIncluded: newCost.isIncluded
      };
      
      if (editingCostId) {
        await fetch(`/api/templates/recurring-costs/${editingCostId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      } else {
        await fetch(`/api/templates/${id}/recurring-costs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      }
      
      setCostDialogOpen(false);
      fetchTemplate();
    } catch (err) {
      console.error('Save cost error:', err);
    }
  };

  const handleDeleteCost = async (costId) => {
    if (!window.confirm('Supprimer ce coût récurrent ?')) return;

    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/templates/recurring-costs/${costId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTemplate();
    } catch (err) {
      console.error('Delete cost error:', err);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const calculateItemTotalHours = (item) => {
    if (item.hours && item.hours.length > 0) {
      return item.hours.reduce((sum, h) => sum + (h.defaultHours || 0), 0);
    }
    return 0;
  };

  const totalMonthlyCost = template?.recurringCosts?.reduce((sum, c) => {
    if (!c.isIncluded) return sum + coutMensuelLigne(c);
    return sum;
  }, 0) || 0;

  if (loading) {
    return <Typography sx={{ p: 3 }}>Chargement...</Typography>;
  }

  if (!template) {
    return <Typography sx={{ p: 3 }}>Template non trouvé</Typography>;
  }

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/admin/templates" color="inherit" underline="hover">
          Templates
        </Link>
        <Typography color="text.primary">{template.name}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          {editingName ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                autoFocus
              />
              <IconButton
                size="small"
                onClick={() => {
                  handleUpdateTemplate({ name: tempName });
                  setEditingName(false);
                }}
              >
                <Save size={18} />
              </IconButton>
            </Stack>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {template.name}
              </Typography>
              <IconButton size="small" onClick={() => setEditingName(true)}>
                <Edit3 size={16} />
              </IconButton>
            </Box>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            {template.projectType && (
              <Chip
                icon={<FolderKanban size={12} />}
                label={template.projectType.name}
                size="small"
                sx={{
                  bgcolor: `${template.projectType.color}15`,
                  color: template.projectType.color,
                  borderColor: `${template.projectType.color}30`,
                  border: '1px solid',
                  '& .MuiChip-icon': { color: template.projectType.color }
                }}
              />
            )}
            <Typography variant="body2" color="text.secondary">
              {template.description || 'Aucune description'}
            </Typography>
          </Stack>
        </Box>
        
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={18} />}
          onClick={() => navigate('/admin/templates')}
        >
          Retour
        </Button>
      </Box>

      {/* Add Section Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => setSectionDialogOpen(true)}
        >
          Ajouter une section
        </Button>
      </Box>

      {/* Sections */}
      <Stack spacing={2}>
        {template.sections?.map((section) => {
          const categoryName = section.category?.name || section.customCategory || 'Sans catégorie';
          const isExpanded = expandedSections[section.id];
          const totalHours = section.items?.reduce((sum, item) => sum + calculateItemTotalHours(item), 0) || 0;

          return (
            <Paper
              key={section.id}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              {/* Section Header */}
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  bgcolor: template.projectType?.color ? `${template.projectType.color}08` : 'rgba(0, 212, 170, 0.03)',
                  borderBottom: isExpanded ? '1px solid rgba(255, 255, 255, 0.06)' : 'none',
                  '&:hover': { bgcolor: template.projectType?.color ? `${template.projectType.color}12` : 'rgba(0, 212, 170, 0.06)' }
                }}
                onClick={() => toggleSection(section.id)}
              >
                <GripVertical size={16} color="#6b7280" style={{ cursor: 'grab' }} />
                
                <IconButton size="small" sx={{ p: 0 }}>
                  {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </IconButton>

                <Box sx={{ color: template.projectType?.color || '#00d4aa', display: 'flex' }}>
                  <Tag size={18} />
                </Box>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {section.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {categoryName} • {section.items?.length || 0} éléments • {totalHours}h
                  </Typography>
                </Box>

                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleDeleteSection(section.id); }}
                  sx={{ color: '#ef4444' }}
                >
                  <Trash2 size={16} />
                </IconButton>
              </Box>

              {/* Section Items */}
              <Collapse in={isExpanded}>
                <Box sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    {section.items?.map((item) => {
                      const itemHours = calculateItemTotalHours(item);
                      
                      return (
                        <Paper
                          key={item.id}
                          sx={{
                            p: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.01)',
                            border: '1px solid rgba(255, 255, 255, 0.04)',
                            borderRadius: 1.5
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <GripVertical size={14} color="#6b7280" style={{ marginTop: 4, cursor: 'grab' }} />
                            
                            <Box sx={{ flexGrow: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {item.name}
                                </Typography>
                                {item.complexity && (
                                  <Chip label={item.complexity} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                                )}
                              </Box>
                              
                              {item.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {item.description}
                                </Typography>
                              )}

                              {/* Hours breakdown - dynamic */}
                              {item.hours && item.hours.length > 0 && (
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  {item.hours.map((h) => {
                                    if (!h.defaultHours || h.defaultHours === 0) return null;
                                    return (
                                      <Chip
                                        key={h.hourTypeId}
                                        label={`${h.hourType?.name || 'Heures'}: ${h.defaultHours}h`}
                                        size="small"
                                        sx={{ 
                                          height: 20, 
                                          fontSize: '0.65rem', 
                                          bgcolor: `${template.projectType?.color || '#00d4aa'}15`, 
                                          color: template.projectType?.color || '#00d4aa' 
                                        }}
                                      />
                                    );
                                  })}
                                </Stack>
                              )}
                            </Box>

                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" sx={{ fontWeight: 600, color: template.projectType?.color || '#00d4aa' }}>
                                {itemHours}h
                              </Typography>
                              <IconButton size="small" onClick={() => openEditItemDialog(section.id, item)}>
                                <Edit3 size={14} />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDeleteItem(item.id)} sx={{ color: '#ef4444' }}>
                                <Trash2 size={14} />
                              </IconButton>
                            </Stack>
                          </Box>
                        </Paper>
                      );
                    })}

                    {section.items?.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        Aucun élément dans cette section
                      </Typography>
                    )}

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Plus size={14} />}
                      onClick={() => openAddItemDialog(section.id)}
                      sx={{ alignSelf: 'flex-start', borderStyle: 'dashed' }}
                    >
                      Ajouter un élément
                    </Button>
                  </Stack>
                </Box>
              </Collapse>
            </Paper>
          );
        })}

        {template.sections?.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', border: '1px dashed rgba(255, 255, 255, 0.1)' }}>
            <Typography color="text.secondary">
              Aucune section. Ajoutez des sections pour définir la structure du template.
            </Typography>
          </Paper>
        )}
      </Stack>

      {/* Recurring Costs Section */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Calendar size={20} color={template.projectType?.color || '#00d4aa'} />
              Coûts mensuels récurrents
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Hébergement, maintenance, abonnements et autres frais mensuels
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Plus size={14} />}
            onClick={openAddCostDialog}
          >
            Ajouter un coût
          </Button>
        </Box>

        {template.recurringCosts && template.recurringCosts.length > 0 ? (
          <Paper sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)', borderRadius: 2 }}>
            <Stack divider={<Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.06)' }} />}>
              {template.recurringCosts.map((cost) => {
                const costTypeName = cost.costType?.name || cost.customType || 'Autre';
                return (
                  <Box key={cost.id} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1, 
                      bgcolor: `${template.projectType?.color || '#00d4aa'}15`,
                      color: template.projectType?.color || '#00d4aa',
                      display: 'flex'
                    }}>
                      <DollarSign size={18} />
                    </Box>
                    
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {cost.name}
                        </Typography>
                        <Chip 
                          label={costTypeName} 
                          size="small" 
                          sx={{ 
                            height: 20, 
                            fontSize: '0.65rem', 
                            bgcolor: `${template.projectType?.color || '#00d4aa'}15`, 
                            color: template.projectType?.color || '#00d4aa' 
                          }}
                        />
                        {cost.isOptional && (
                          <Chip label="Optionnel" size="small" sx={{ height: 20, fontSize: '0.65rem' }} variant="outlined" />
                        )}
                        {cost.isIncluded && (
                          <Chip label="Inclus" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }} />
                        )}
                      </Box>
                      {cost.description && (
                        <Typography variant="body2" color="text.secondary">
                          {cost.description}
                        </Typography>
                      )}
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: cost.isIncluded ? '#10b981' : (template.projectType?.color || '#00d4aa') }}>
                      {cost.isIncluded
                        ? 'Inclus'
                        : `${afficheQuantite(cost) ? `${cost.quantity} × ${formatMontant(cost.monthlyCost)}$  ` : ''}${formatMontant(coutMensuelLigne(cost))}$/mois`}
                    </Typography>

                    <Stack direction="row" spacing={0.5}>
                      <IconButton size="small" onClick={() => openEditCostDialog(cost)}>
                        <Edit3 size={14} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteCost(cost.id)} sx={{ color: '#ef4444' }}>
                        <Trash2 size={14} />
                      </IconButton>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>

            {/* Total */}
            <Box sx={{ p: 2, bgcolor: `${template.projectType?.color || '#00d4aa'}08`, borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">Total mensuel</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: template.projectType?.color || '#00d4aa' }}>
                  {formatMontant(totalMonthlyCost)}$/mois
                </Typography>
              </Stack>
            </Box>
          </Paper>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center', border: '1px dashed rgba(255, 255, 255, 0.1)' }}>
            <Typography color="text.secondary">
              Aucun coût récurrent. Ajoutez des frais mensuels comme l'hébergement ou la maintenance.
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Add Section Dialog */}
      <Dialog open={sectionDialogOpen} onClose={() => setSectionDialogOpen(false)} PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none', minWidth: 450 } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Nouvelle section</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Catégorie</InputLabel>
              <Select 
                value={newSection.categoryId} 
                label="Catégorie" 
                onChange={(e) => setNewSection({ ...newSection, categoryId: e.target.value })}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
                <MenuItem value="other">
                  <em>Autre (saisie libre)</em>
                </MenuItem>
              </Select>
            </FormControl>
            
            {newSection.categoryId === 'other' && (
              <TextField 
                fullWidth 
                size="small" 
                label="Catégorie personnalisée" 
                value={newSection.customCategory} 
                onChange={(e) => setNewSection({ ...newSection, customCategory: e.target.value })} 
              />
            )}
            
            <TextField 
              fullWidth 
              size="small" 
              label="Nom de la section" 
              value={newSection.name} 
              onChange={(e) => setNewSection({ ...newSection, name: e.target.value })} 
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setSectionDialogOpen(false)} color="inherit">Annuler</Button>
          <Button variant="contained" onClick={handleAddSection} disabled={!newSection.name.trim()}>Ajouter</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Item Dialog */}
      <Dialog open={itemDialogOpen} onClose={() => setItemDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>{editingItemId ? 'Modifier l\'élément' : 'Nouvel élément'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField fullWidth size="small" label="Nom" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} required />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Complexité</InputLabel>
                <Select value={newItem.complexity} label="Complexité" onChange={(e) => setNewItem({ ...newItem, complexity: e.target.value })}>
                  {complexityOptions.map(opt => (<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>))}
                </Select>
              </FormControl>
            </Stack>

            <TextField fullWidth size="small" multiline rows={2} label="Description" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} />

            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Clock size={12} />
              HEURES PAR DÉFAUT
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {newItem.hours.map((h, index) => (
                <TextField
                  key={h.hourTypeId}
                  size="small"
                  type="number"
                  label={h.hourTypeName}
                  value={h.defaultHours}
                  onChange={(e) => {
                    const newHours = [...newItem.hours];
                    newHours[index] = { ...newHours[index], defaultHours: parseFloat(e.target.value) || 0 };
                    setNewItem({ ...newItem, hours: newHours });
                  }}
                  inputProps={{ min: 0, step: 0.5 }}
                  sx={{ width: 120 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">h</InputAdornment>
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setItemDialogOpen(false)} color="inherit">Annuler</Button>
          <Button variant="contained" onClick={handleSaveItem} disabled={!newItem.name.trim()}>
            {editingItemId ? 'Enregistrer' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Recurring Cost Dialog */}
      <Dialog open={costDialogOpen} onClose={() => setCostDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>{editingCostId ? 'Modifier le coût' : 'Nouveau coût récurrent'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Type de coût</InputLabel>
              <Select 
                value={newCost.costTypeId} 
                label="Type de coût" 
                onChange={(e) => handleCostTypeChange(e.target.value)}
              >
                {costTypes.map((ct) => (
                  <MenuItem key={ct.id} value={ct.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <span>{ct.name}</span>
                      {ct.defaultCost > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {ct.defaultCost}$/mois
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
                <MenuItem value="other">
                  <em>Autre (saisie libre)</em>
                </MenuItem>
              </Select>
            </FormControl>

            {newCost.costTypeId === 'other' && (
              <TextField 
                fullWidth 
                size="small" 
                label="Type personnalisé" 
                value={newCost.customType} 
                onChange={(e) => setNewCost({ ...newCost, customType: e.target.value })} 
              />
            )}

            <TextField 
              fullWidth 
              size="small" 
              label="Nom" 
              value={newCost.name} 
              onChange={(e) => setNewCost({ ...newCost, name: e.target.value })} 
              placeholder="Ex: Hébergement K8s, API Stripe..."
            />

            <TextField 
              fullWidth 
              size="small" 
              multiline 
              rows={2} 
              label="Description" 
              value={newCost.description} 
              onChange={(e) => setNewCost({ ...newCost, description: e.target.value })} 
            />

            <TextField
              fullWidth
              size="small"
              type="number"
              label="Coût mensuel unitaire"
              value={newCost.monthlyCost}
              onChange={(e) => setNewCost({ ...newCost, monthlyCost: parseFloat(e.target.value) || 0 })}
              inputProps={{ min: 0, step: 5 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><DollarSign size={16} /></InputAdornment>,
                endAdornment: <InputAdornment position="end">/mois</InputAdornment>
              }}
            />

            {/* La quantite multiplie le prix unitaire, comme sur un devis. */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
              <TextField
                size="small"
                type="number"
                label="Quantité"
                value={newCost.quantity}
                onChange={(e) => setNewCost({
                  ...newCost,
                  quantity: Math.max(1, parseInt(e.target.value, 10) || 1)
                })}
                inputProps={{ min: 1, step: 1 }}
                sx={{ width: 130 }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                = {formatMontant(coutMensuelLigne(newCost))}$/mois
              </Typography>
            </Stack>

            {newCost.quantity > 1 && (
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={
                  <Switch
                    size="small"
                    checked={newCost.showQuantity}
                    onChange={(e) => setNewCost({ ...newCost, showQuantity: e.target.checked })}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Afficher « {newCost.quantity} × {formatMontant(newCost.monthlyCost)}$ » au client
                  </Typography>
                }
              />
            )}

            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newCost.isOptional}
                    onChange={(e) => setNewCost({ ...newCost, isOptional: e.target.checked, isIncluded: false })}
                  />
                }
                label="Optionnel"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newCost.isIncluded}
                    onChange={(e) => setNewCost({ ...newCost, isIncluded: e.target.checked, isOptional: false })}
                  />
                }
                label="Inclus"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCostDialogOpen(false)} color="inherit">Annuler</Button>
          <Button variant="contained" onClick={handleSaveCost} disabled={!newCost.name.trim()}>
            {editingCostId ? 'Enregistrer' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TemplateDetail;
